import { useState, useRef, useEffect } from 'react';
import { formatUsd, getDisplayCountry } from '../../data/countries';
import apiClient from '../../utils/api';

export const loadStripeJs = () => {
  if (window.Stripe) return Promise.resolve(window.Stripe);
  return new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = "https://js.stripe.com/v3/";
    s.onload = () => resolve(window.Stripe);
    s.onerror = reject;
    document.head.appendChild(s);
  });
};

const DepositForm = ({
  onConfirm,
  onStripeDeposit,
  country,
  currentBalance = 0,
  testDepositsEnabled = false,
  stripeEnabled = false,
  stripePublishableKey = "",
  depositing = false,
}) => {
  const cardAvailable = stripeEnabled && Boolean(stripePublishableKey);
  const testAvailable = testDepositsEnabled;
  const [amount, setAmount] = useState(1000);
  const [mode, setMode] = useState(cardAvailable ? "card" : testAvailable ? "test" : "none");
  const paymentMountRef = useRef(null);
  const stripeElementsRef = useRef(null);
  const c = getDisplayCountry(country);
  const usdAmount = parseFloat(amount) || 0;
  const newBalance = currentBalance + usdAmount;
  const localHint = usdAmount * c.rate;

  useEffect(() => {
    if (cardAvailable && mode !== "card") setMode("card");
    else if (!cardAvailable && testAvailable && mode === "none") setMode("test");
  }, [cardAvailable, testAvailable, mode]);

  const startCardDeposit = async () => {
    if (!stripePublishableKey || !onStripeDeposit) return;
    try {
      const res = await apiClient.createStripeDepositIntent(usdAmount);
      const StripeCtor = await loadStripeJs();
      const stripe = StripeCtor(stripePublishableKey);
      const elements = stripe.elements({ clientSecret: res.data.clientSecret });
      stripeElementsRef.current = { stripe, elements };
      if (paymentMountRef.current) paymentMountRef.current.innerHTML = "";
      const el = elements.create("payment");
      el.mount(paymentMountRef.current);
      paymentMountRef.current.dataset.ready = "1";
    } catch (e) {
      onStripeDeposit?.({ error: e.message });
    }
  };

  useEffect(() => {
    if (mode === "card" && cardAvailable) startCardDeposit();
  }, [mode, usdAmount, cardAvailable]);

  return (
    <div className="deposit-form">
      <p className="deposit-form__lead">
        Top up your <strong>CoOwn USD wallet</strong> to invest in properties. Card and bank transfer (ACH) are processed securely by Stripe when enabled.
      </p>
      <div className="form-group">
        <label>Amount (USD)</label>
        <input type="number" min="1" step="1" value={amount} onChange={(e) => setAmount(parseFloat(e.target.value) || 0)} />
      </div>
      <div className="deposit-amount-row">
        {[500, 1000, 5000, 10000].map((a) => (
          <button
            key={a}
            type="button"
            className={`deposit-amount-btn${Number(amount) === a ? " selected" : ""}`}
            onClick={() => setAmount(a)}
            aria-pressed={Number(amount) === a}
          >
            {formatUsd(a, { maxDecimals: 0 })}
          </button>
        ))}
      </div>
      {cardAvailable && testAvailable ? (
        <div className="deposit-form__mode-toggle">
          <button type="button" className={`btn btn-sm ${mode === "card" ? "btn-primary" : "btn-outline"}`} onClick={() => setMode("card")}>
            Card / bank
          </button>
          <button type="button" className={`btn btn-sm ${mode === "test" ? "btn-primary" : "btn-outline"}`} onClick={() => setMode("test")}>
            Test (staging)
          </button>
        </div>
      ) : null}
      <p className="deposit-form__balance">
        Current balance: <strong>{formatUsd(currentBalance)}</strong>
        {mode === "test" && testAvailable ? (
          <>
            {" → "}
            After deposit: <strong>{formatUsd(newBalance)}</strong>
          </>
        ) : null}
        {c.currency !== "USD" && usdAmount > 0 ? (
          <span className="deposit-form__local-hint">
            ≈ {c.symbol}{localHint.toLocaleString(undefined, { maximumFractionDigits: 0 })} {c.currency} (indicative)
          </span>
        ) : null}
      </p>
      {mode === "card" && cardAvailable ? (
        <>
          <div ref={paymentMountRef} className="deposit-form__stripe" />
          <button
            type="button"
            className="btn btn-primary deposit-form__submit"
            disabled={depositing || usdAmount <= 0}
            onClick={async () => {
              try {
                const { stripe, elements } = stripeElementsRef.current || {};
                if (!stripe || !elements) throw new Error("Payment form is still loading. Wait a moment and try again.");
                const { error } = await stripe.confirmPayment({ elements, redirect: "if_required" });
                if (error) throw new Error(error.message);
                onStripeDeposit?.({ success: true, amount: usdAmount });
              } catch (e) {
                onStripeDeposit?.({ error: e.message });
              }
            }}
          >
            <i className={`fas fa-${depositing ? "spinner fa-spin" : "lock"}`} aria-hidden="true"></i>
            {depositing ? "Processing…" : `Add ${formatUsd(usdAmount)} — card or bank`}
          </button>
        </>
      ) : null}
      {mode === "test" && testAvailable ? (
        <button
          type="button"
          className="btn btn-outline deposit-form__submit"
          disabled={depositing || usdAmount <= 0}
          onClick={() => onConfirm(usdAmount)}
        >
          <i className={`fas fa-${depositing ? "spinner fa-spin" : "flask"}`} aria-hidden="true"></i>
          {depositing ? "Processing…" : "Add test funds (staging only)"}
        </button>
      ) : null}
      {!cardAvailable && !testAvailable ? (
        <div className="deposit-form__unconfigured">
          <p><strong>Deposits are not configured yet.</strong></p>
        </div>
      ) : null}
      {cardAvailable ? (
        <p className="deposit-form__footnote">
          Payments secured by Stripe. Bank transfer (ACH) appears when enabled in your Stripe account.
        </p>
      ) : testAvailable ? (
        <p className="deposit-form__footnote">
          Staging mode — test funds credit instantly. Configure Stripe for production deposits.
        </p>
      ) : null}
    </div>
  );
};

export default DepositForm;
